import { getWebApp } from '../telegram/sdk'
import type { ProgressStore } from './types'

/**
 * Telegram CloudStorage - до 1024 ключей, значение до 4096 байт.
 * API колбэчный, оборачиваем в промисы.
 *
 * Каждый вызов ограничен таймаутом: если клиент Telegram не ответит,
 * колбэк не вызовется никогда, и любое await-ожидание повиснет навсегда.
 * Лучше отдать null и работать дальше, чем показать пустой экран.
 */

const TIMEOUT_MS = 3000

function withTimeout<T>(run: (resolve: (v: T) => void, reject: (e: Error) => void) => void, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    let settled = false
    const done = (value: T) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve(value)
    }
    const timer = setTimeout(() => done(fallback), TIMEOUT_MS)
    try {
      run(done, () => done(fallback))
    } catch {
      done(fallback)
    }
  })
}

export class CloudStore implements ProgressStore {
  private get cs() {
    const cs = getWebApp().CloudStorage
    if (!cs) throw new Error('CloudStorage недоступен')
    return cs
  }

  get(key: string): Promise<string | null> {
    return withTimeout<string | null>((resolve) => {
      this.cs.getItem(key, (err, value) => {
        resolve(err || value === undefined || value === '' ? null : value)
      })
    }, null)
  }

  async set(key: string, value: string): Promise<void> {
    await withTimeout<boolean>((resolve) => {
      this.cs.setItem(key, value, () => resolve(true))
    }, false)
  }

  async remove(key: string): Promise<void> {
    await withTimeout<boolean>((resolve) => {
      this.cs.removeItem(key, () => resolve(true))
    }, false)
  }

  keys(): Promise<string[]> {
    return withTimeout<string[]>((resolve) => {
      this.cs.getKeys((err, keys) => resolve(err ? [] : (keys ?? [])))
    }, [])
  }
}
